import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { Component, inject, input, model, OnInit, output } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { SearchHelper } from "../../shared/helpers/SearchHelper";
import { IFilterDef, ISelectedFilterDef, IFilterCategoryDef } from "../../shared/interfaces/IFilterDef";
import { SearchType } from "../../shared/services/search-storage.service";
import { DateAdapter, MAT_DATE_FORMATS, MatOptionModule } from '@angular/material/core';
import { AppDateAdapter, APP_DATE_FORMATS } from "../../shared/format-datepicker";
import { ICodeItem } from "../../shared/interfaces/ICodeItem";
import { MatChipEditedEvent, MatChipsModule } from '@angular/material/chips';
import { MatCardModule } from "@angular/material/card";
import { MatMenuModule } from "@angular/material/menu";
import { MatDividerModule } from "@angular/material/divider";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatIconModule } from "@angular/material/icon";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatTooltipModule } from "@angular/material/tooltip";
import { CommonModule } from "@angular/common";
import { TypeaheadMultiComponent } from "../typeahead/typeahead.component";

@Component({
  selector: 'filter-card',
  templateUrl: 'filter-card.component.html',
  providers: [
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS }
  ],
  standalone: true,
  imports: [CommonModule, FormsModule, MatCardModule, MatMenuModule, MatChipsModule, MatDividerModule,
    MatFormFieldModule, TypeaheadMultiComponent, MatSelectModule,
    MatOptionModule, MatInputModule, MatButtonModule, MatProgressSpinnerModule, MatIconModule,
    MatDatepickerModule, MatTooltipModule]
})
export class FilterCardComponent<TSearchCriteria> implements OnInit {
  private searchHelper = inject(SearchHelper);

  public SearchHistoryType = input.required<SearchType>();
  public filterCategories = input.required<IFilterCategoryDef<TSearchCriteria>[]>();
  public selectedFilters = model<ISelectedFilterDef<TSearchCriteria>[]>([]);
  public enableFavorites = input<boolean>(false);
  public OnSearchClicked = output<ISelectedFilterDef<TSearchCriteria>[]>();
  public OnCollapseSearchToggled = output();
  public OnClearSearch = output();
  public OnClearFavorites = output();

  protected readonly separatorKeyCodes = [ENTER, COMMA] as const;
  isLoading: boolean = false;

  ngOnInit(): void {
    if (this.filterCategories()) {
      this.filterCategories().forEach((category) => {
        category.filters.sort((a, b) => (a.name > b.name) ? 1 : ((b.name > a.name) ? -1 : 0));
      });
    }
  }

  protected areAllFiltersApplied = () =>
    this.filterCategories().every((category) => category.filters.every((a) => !a.disabled && this.isFilterInUse(a)));

  protected doesCategoryHaveAvailableFilters = (filterCategory: IFilterCategoryDef<TSearchCriteria>) =>
    filterCategory.filters.find((a) => !a.disabled && !this.isFilterInUse(a)) !== undefined;

  protected ToggleSearchSection() {
    this.OnCollapseSearchToggled.emit();
  }

  private isFilterInUse(filterDef: IFilterDef<TSearchCriteria>) {
    if (this.selectedFilters() == null)
      return false;

    if (this.selectedFilters().map(e => e.filter.name).indexOf(filterDef.name) < 0) {
      return false;
    }
    else {
      return true;
    }
  }

  addFilter = (filterDef: IFilterDef<TSearchCriteria>) => {
    if (this.isFilterInUse(filterDef)) {
      return;
    }

    if (filterDef.disabled == true) {
      return;
    }

    this.selectedFilters.update((current) => {
      const safeCurrent = current ?? [];
      return [...safeCurrent, { filter: filterDef, searchValue: filterDef.defaultValue }];
    });
  }

  removeFilter = (selectedItem: ISelectedFilterDef<TSearchCriteria>) => {
    this.selectedFilters.update((current) => (current ?? []).filter((a) => a !== selectedItem));
  }

  public OnSearchButtonClick = () => {
    // Ensure ngModel-driven in-place edits (e.g. selectedFilter.searchValue) are propagated
    // to the parent via the model's change output by publishing a new array reference.
    this.selectedFilters.set([...(this.selectedFilters() ?? [])]);

    this.OnSearchClicked.emit(this.selectedFilters());
    this.searchHelper.setLatestFiltersV2(this.SearchHistoryType(), this.selectedFilters());
  }

  public clearFavorites = () => {
    if (this.OnClearFavorites)
      this.OnClearFavorites.emit();
  }

  clearSearch = () => {
    this.selectedFilters.set([]);
    if (this.OnClearSearch)
      this.OnClearSearch.emit();
  }

  protected async fetchPreviousSearchParameters() {
    this.isLoading = true;
    const filters: IFilterDef<TSearchCriteria>[] = [];
    this.filterCategories().forEach((category) => {
      category.filters.forEach((filter) => filters.push(filter));
    });

    const savedSearch = await this.searchHelper.getLatestFiltersV2(this.SearchHistoryType(), filters);
    if (savedSearch != null)
      this.selectedFilters.set(savedSearch);
    this.isLoading = false;
  }

  protected addChip(event: MatChipEditedEvent, filterName: string): void {
    const value = (event.value || '').trim();

    if (value) {
      this.selectedFilters.update((current) =>
        (current ?? []).map((selectedFilter) => {
          if (selectedFilter.filter.name !== filterName)
            return selectedFilter;

          const existing = Array.isArray(selectedFilter.searchValue) ? selectedFilter.searchValue : [];
          return { ...selectedFilter, searchValue: [...existing, value] };
        })
      );
    }

    // Clear the input value
    event.value = "";
  }

  protected removeChip(chip: ICodeItem, filterName: string) {
    this.selectedFilters.update((current) =>
      (current ?? []).map((selectedFilter) => {
        if (selectedFilter.filter.name !== filterName)
          return selectedFilter;

        if (!Array.isArray(selectedFilter.searchValue))
          return selectedFilter;

        const updated = (selectedFilter.searchValue as ICodeItem[]).filter((a) => a !== chip);
        return { ...selectedFilter, searchValue: updated };
      })
    );
  }
}
